/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/prefer-default-export */
import moment from 'moment';
import { useMemo } from 'react';
import {
  BUFFER_AUTHORIZATION_TYPES,
  BUFFER_AUTHORIZATION_TYPES_MAP,
  CardMastercardTransactionResponseCodes,
  CardMastercardTransactionStatuses,
  cardProviders,
} from '../../screens/Card/constants';
import { exchange } from '../constants';
import { formatNumber } from '../formatHelpers';
import { useCardStatus, useUserCardProvider } from './user';
import { isObject } from '../helpers';

export const useCardTransactionBridge = (data) => {
  const { cardProvider } = useUserCardProvider();
  const { data: cardStatus } = useCardStatus();
  const isRefundAuthorization = (item) =>
    item?.responseCode === CardMastercardTransactionResponseCodes.APPROVED &&
    (item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.AUTH_REFUND ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.REFUND_REVERSAL ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.REFUND_CLEARING ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.REFUND_TRANSACTION ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.REFUND_PARTIAL_REVERSAL ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.AUTH_REFUND_REVERSAL);

  const getBufferAuthorizationResponse = (item, index, { isPrimary, merchant }) => {
    const primaryNumber = `${!isPrimary ? '-' : '+'}${formatNumber(
      item?.amountTransactionCurrency,
      exchange.FIAT_VALUE
    )}`;

    const icon = isPrimary ? 'icon-icome-navigation' : 'icon-outcome-navigation';

    const amountSymbol = `${!isPrimary ? '-' : ''}`;

    const displayAmount = `${amountSymbol}${formatNumber(
      item?.amountTransactionCurrency,
      exchange.FIAT_VALUE
    )} ${item?.transactionCurrency?.toUpperCase()}`;

    const originalDisplayAmount = `${amountSymbol}${formatNumber(
      item?.amountInOriginalCurrency,
      exchange.FIAT_VALUE
    )} ${item?.originalTransactionCurrency?.toUpperCase()}`;

    const mastercardMarketRate = `1 ${item?.originalTransactionCurrency?.toUpperCase()} = ${formatNumber(
      item?.billingConversionRate,
      7
    )} ${item?.transactionCurrency?.toUpperCase()}`;

    const ecbMarketRate = item?.ecb_rate
      ? `1 ${item?.originalTransactionCurrency?.toUpperCase()} = ${formatNumber(
          item?.ecb_rate,
          7
        )} ${item?.transactionCurrency?.toUpperCase()}`
      : null;

    const programMarkup = `${formatNumber(
      ((item?.billingConversionRate - item?.ecb_rate) / item?.ecb_rate) * 100,
      exchange.FIAT_VALUE
    )}%`;

    const cashbackAmount = `${formatNumber(item?.cashback?.amount, exchange.CRYPTO_VALUE)} ${
      item?.cashback?.currency
    }  `;

    let transactionStatus;
    if (
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.AUTH_REVERSAL ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.AUTH_REFUND_REVERSAL ||
      item?.bufferAuthorizationType === BUFFER_AUTHORIZATION_TYPES.REFUND_REVERSAL
    ) {
      transactionStatus = CardMastercardTransactionStatuses.DECLINED;
    } else if (item?.transactionStatus === CardMastercardTransactionStatuses.CONFIRMED) {
      transactionStatus = 'completed';
    } else {
      transactionStatus = item?.transactionStatus;
    }

    return {
      index,
      id: item?.id,
      date: moment(item?.transactionDateTime).format('D MMMM YYYY, h:mm:ss A'),
      formatedDate: moment(item?.transactionDateTime).format('DD MMM YYYY'),
      merchant,
      merchantType: item?.merchantType,
      amount: item?.amountTransactionCurrency,
      displayAmount,
      originalDisplayAmount,
      transactionCurrency: item?.transactionCurrency,
      sourceOfFunds: item?.sourceOfFunds,
      status: item?.transactionStatus,
      isPrimary,
      icon,
      primaryNumber,
      additionalInfo: item?.transactionCurrency !== item?.originalTransactionCurrency,
      mastercardMarketRate,
      ecbMarketRate,
      programMarkup,
      cashbackAmount,
      transactionType:
        BUFFER_AUTHORIZATION_TYPES_MAP[item.bufferAuthorizationType] ||
        BUFFER_AUTHORIZATION_TYPES_MAP.AUTH_DEBIT,
      transactionStatus,
      panLast4: item?.panLast4,
    };
  };

  const getMonavateResponse = (item, index) =>
    getBufferAuthorizationResponse(item, index, {
      isPrimary: isRefundAuthorization(item),
      merchant: item?.merchantNameLocation?.split(',')[0],
    });

  const getConnectFinancialResponse = (item, index) => {
    const response = getBufferAuthorizationResponse(item, index, {
      isPrimary:
        item?.operationType === 'CREDIT' &&
        item?.transactionStatus !== CardMastercardTransactionStatuses.DECLINED,
      merchant: item?.merchantNameLocation,
    });
    return {
      ...response,
      transactionType:
        item?.operationType === 'CREDIT'
          ? BUFFER_AUTHORIZATION_TYPES_MAP.AUTH_CREDIT
          : BUFFER_AUTHORIZATION_TYPES_MAP.AUTH_DEBIT,
      panLast4: item?.panLast4?.replace(/[^0-9]/g, '').slice(-4),
    };
  };

  const getGalileoResponse = (item, index) => {
    const amountForDisplay = `${formatNumber(
      item?.amt,
      exchange.FIAT_VALUE
    )} ${cardStatus?.currency?.toUpperCase()}`;

    let icon = '';
    if (item?.amt > 0) {
      icon = 'icon-icome-navigation';
    } else if (item?.amt < 0) {
      icon = 'icon-outcome-navigation';
    }

    const primaryNumber = `${item?.amt > 0 ? '+' : ''}${formatNumber(
      item?.amt,
      exchange.FIAT_VALUE
    )}`;

    return {
      index,
      id: item?.source_id,
      date: moment(item?.post_ts).format('D MMMM YYYY, h:mm:ss A'),
      formatedDate: moment(item?.post_ts).format('MMM DD YYYY'),
      merchant: item?.formatted_merchant_desc,
      merchantType: item?.description,
      amount: item?.amt,
      details: item?.details,
      displayAmount: amountForDisplay,
      transactionCurrency: cardStatus?.currency,
      status: '',
      isPrimary: item?.amt > 0,
      icon,
      primaryNumber,
      sourceOfFunds: item?.sourceOfFunds,
    };
  };

  const trasformedData = useMemo(
    () =>
      data.map((item, index) => {
        switch (cardProvider) {
          case cardProviders.MONAVATE: {
            return getMonavateResponse(item, index);
          }

          case cardProviders.GALILEO: {
            return getGalileoResponse(item, index);
          }

          case cardProviders.CONNECT_FINANCIAL: {
            return getConnectFinancialResponse(item, index);
          }

          default:
            return {
              id: '',
              date: '',
              icon: '',
              card: '',
              merchant: '',
              amount: '',
              displayAmount: '',
              currency: '',
              status: '',
            };
        }
      }),
    [cardProvider, data, cardStatus, getGalileoResponse]
  );

  const filteredTransformedData = useMemo(() => {
    if (trasformedData) {
      return trasformedData?.filter((item) => isObject(item));
    }
    return [];
  }, [trasformedData]);

  return { transactions: filteredTransformedData };
};

export const useCardHolderNameForUser = (user) => {
  const { cardProvider } = useUserCardProvider();

  const [userFirstName, userLastName] = useMemo(
    () => [user.first_name, user.last_name],
    [user, cardProvider]
  );

  return [userFirstName, userLastName];
};
